/**
 * Requirements Summary — shared calendar-date helpers (DRY).
 *
 * Canonical home for the strict `YYYY-MM-DD` regex + `Date|string → YYYY-MM-DD`
 * normalization. The same `/^\d{4}-\d{2}-\d{2}$/` pattern is currently
 * re-declared in ~20+ files (e.g. `utils/closings/internal.ts`,
 * `utils/closings/pickClosingForCell.ts`); new date-window code should import
 * from HERE, and those sites can converge on this module over time so the
 * pattern stops accumulating new copies. First consumer: Athlete Standing
 * Discounts' `resolveAthleteStandingDiscount` window check (#660, U2).
 *
 * ⚠️ TIMEZONE — this module does NOT convert timezones. `toCalendarDateString`
 * reduces a `Date` to its **UTC** calendar day. Code that needs a gym-LOCAL
 * calendar day (e.g. the KTD-8 billing-window check for athlete standing
 * discounts) MUST convert the instant to the gym timezone FIRST and pass the
 * resulting `YYYY-MM-DD` string — passing a raw `new Date()` here yields the
 * server's UTC day, which can be one day off from the gym's local day at a
 * timezone boundary.
 */

/** Matches a strict `YYYY-MM-DD` calendar-date string. */
export const YYYY_MM_DD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalize a value to a `YYYY-MM-DD` calendar-date string for lexicographic
 * window comparison, or `null` when it cannot be parsed.
 *
 * - A string already in `YYYY-MM-DD` form (the common case — callers pass the
 *   billing month / lesson date) is returned verbatim.
 * - A longer ISO string (`"2026-06-15T12:00:00Z"`) is truncated to its date part.
 * - A `Date` is reduced to its **UTC** calendar day (see the module-level
 *   timezone note — this is a fallback; prefer handing in a gym-local string).
 */
export function toCalendarDateString(value: Date | string): string | null {
  if (typeof value === 'string') {
    if (YYYY_MM_DD_REGEX.test(value)) return value;
    // Tolerate a full ISO timestamp ("2026-06-15T12:00:00Z") by taking its date part.
    const head = value.slice(0, 10);
    return YYYY_MM_DD_REGEX.test(head) ? head : null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getUTCFullYear();
    const m = String(value.getUTCMonth() + 1).padStart(2, '0');
    const d = String(value.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return null;
}
